<script setup lang="ts">
/**
 * Shared title block for blocks that show a headline: optional eyebrow pill,
 * a site-display title, and an optional muted subtitle — all centered.
 *
 * Pulled out of the dozen blocks that were each hand-rolling the same
 * `<h3 class="site-display ... text-2xl tracking-tight sm:text-3xl">` markup,
 * so a consuming app only has to restyle one component (or override its
 * `--site-*` tokens) to match its own section-heading treatment everywhere
 * at once, instead of chasing it through every block.
 *
 * Deliberately renders nothing (not even a wrapping div) when neither
 * `eyebrow` nor `title` is set, so callers can pass straight through from
 * optional block data without an extra `v-if`. The caller supplies its own
 * bottom margin via `class` — each block's spacing before its content varies
 * (mb-3 to mb-8), and this component fully falls through `class`/`style`
 * since it has a single root element.
 */
defineProps<{
  eyebrow?: string | null
  title?: string | null
  subtitle?: string | null
}>()
</script>

<template>
  <div
    v-if="eyebrow || title"
    class="text-center"
  >
    <p
      v-if="eyebrow"
      class="mb-3 inline-flex items-center gap-1.5 rounded-full border border-[var(--site-border)] bg-[var(--site-accent-soft)] px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--site-accent)]"
    >
      {{ eyebrow }}
    </p>
    <h3
      v-if="title"
      class="site-display text-2xl tracking-tight sm:text-3xl"
    >
      {{ title }}
    </h3>
    <p
      v-if="subtitle"
      class="mx-auto mt-3 max-w-2xl text-[var(--site-muted)]"
    >
      {{ subtitle }}
    </p>
  </div>
</template>
